import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";

import MediaLibraryFilters from "./MediaLibraryFilters";
import MediaLibraryItemDragLayer from "./MediaLibraryItemDragLayer";
import MediaLibraryItemList from "./MediaLibraryItemList";
import MediaLibraryOrder from "./MediaLibraryOrder";
import MediaLibraryRangeDuration from "./MediaLibraryRangeDuration";
import MediaLibrarySearch from "./MediaLibrarySearch";
import MediaAPI from "../../../api/MediaAPI";
import { MEDIA_LIBRARY_LIST_WIDTH } from "../../../constants/fast";
import { isDurationFilter } from "../../../utils/typePredicate";

import type { JSX } from "react";

import type { Order } from "./MediaLibraryOrder";
import type {
  DurationFilter,
  FormatFilter,
  IndexFastMediaItem,
} from "../../../api/MediaAPI.types";
import type { MediaFormatWithAdBreak } from "../../../constants/media";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";

export const GET_FAST_MEDIA_LIST_QUERY_KEY = "getFastIndexMediaList";

export interface MediaLibraryFilterState {
  isFilterActive: boolean;
  search: string;
  mediaCount: number;
  filter: {
    format?: FormatFilter;
    duration?: DurationFilter;
  };
  order: Order;
}

interface MediaLibraryProps {
  defaultFormatFilter?: FormatFilter;
  isCustomDragLayer?: boolean;
  isDraggingDisabled?: boolean;
  durationFilterList?: number[];
  onFilterStateChange?: (filterState: MediaLibraryFilterState) => void;
}

function MediaLibrary({
  defaultFormatFilter,
  isCustomDragLayer,
  isDraggingDisabled,
  durationFilterList,
  onFilterStateChange,
}: MediaLibraryProps): JSX.Element {
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const [search, setSearch] = useState<string>("");
  const [formatFilter, setFormatFilter] = useState<MediaFormatWithAdBreak[]>(
    [],
  );
  const [inputValue, setInputValue] = useState<string>("");
  const [order, setOrder] = useState<Order>({
    direction: "up",
    field: "label",
  });

  const initialiseDurationFilter = (
    durationFilter?: number[],
  ): DurationFilter | undefined => {
    const durationFilterSorted = durationFilter?.sort((a, b) => a - b);
    const updatedDurationFilterList = {
      min: durationFilterSorted?.[0],
      max: durationFilterSorted?.[durationFilterSorted.length - 1],
    };
    if (isDurationFilter(updatedDurationFilterList)) {
      return {
        min: updatedDurationFilterList.min,
        max: updatedDurationFilterList.max,
      };
    }
    return undefined;
  };
  const [durationFilter, setDurationFilter] = useState<
    DurationFilter | undefined
  >(initialiseDurationFilter(durationFilterList));

  const initialDurationFilter = useMemo(
    () => initialiseDurationFilter(durationFilterList),
    [durationFilterList],
  );

  const filter = useMemo(() => {
    if (formatFilter.length === 0) {
      return {
        format: defaultFormatFilter,
        duration: durationFilter,
      };
    }
    return {
      format: { includes: formatFilter },
      duration: durationFilter,
    };
  }, [defaultFormatFilter, formatFilter, durationFilter]);

  const isFilterActive = useMemo(() => {
    const hasSearch = search !== "";
    const hasFormatFilter = formatFilter.length > 0;
    const hasDurationChanged =
      durationFilter?.min !== initialDurationFilter?.min ||
      durationFilter?.max !== initialDurationFilter?.max;
    return hasSearch || hasFormatFilter || hasDurationChanged;
  }, [search, formatFilter, durationFilter, initialDurationFilter]);

  const getMediasMetadataListQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getMediasMetadataList,
    params: [],
  });

  const { data: metadataData, isLoading: isLoadingMetadata } = useQuery(
    getMediasMetadataListQuery,
  );

  const mediaFormatList = metadataData?.formats;

  const {
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    data: getFastIndexMediaList,
  } = useInfiniteQuery({
    queryKey: [GET_FAST_MEDIA_LIST_QUERY_KEY, { search, filter, order }],
    queryFn: async (queryParams) =>
      MediaAPI.getFastIndexMediaList({
        channelId,
        page: queryParams.pageParam,
        perPage: 100,
        search,
        filter,
        order,
      }),
    getNextPageParam: (lastPage: {
      page: { number: number; total_items: number; total: number };
      data: IndexFastMediaItem[];
    }) => {
      if (lastPage.page.number < lastPage.page.total) {
        return lastPage.page.number + 1;
      }

      return undefined;
    },
  });

  const mediasList =
    getFastIndexMediaList?.pages.flatMap((page) => page.data) ?? [];

  useEffect(() => {
    onFilterStateChange?.({
      isFilterActive,
      mediaCount: mediasList.length,
      search,
      filter,
      order,
    });
  }, [
    isFilterActive,
    search,
    filter,
    order,
    onFilterStateChange,
    mediasList.length,
  ]);

  const handleFilterChange = useCallback(
    (filterList: MediaFormatWithAdBreak[]): void => {
      setFormatFilter(filterList);
    },
    [],
  );

  const hasDurationFilter = durationFilterList && durationFilter;

  return (
    <React.Fragment>
      {isCustomDragLayer ? <MediaLibraryItemDragLayer /> : null}
      <div
        className="flex h-full flex-col"
        style={{ width: MEDIA_LIBRARY_LIST_WIDTH }}
      >
        <div className="flex gap-[0.3125rem]">
          <MediaLibrarySearch
            inputValue={inputValue}
            setInputValue={setInputValue}
            setSearch={setSearch}
          />
          <MediaLibraryOrder order={order} setOrder={setOrder} />
        </div>
        <MediaLibraryFilters
          defaultFormatFilter={defaultFormatFilter}
          isLoading={isLoadingMetadata}
          mediaFormatList={mediaFormatList}
          onChange={handleFilterChange}
        />
        {hasDurationFilter ? (
          <MediaLibraryRangeDuration
            durationFilter={durationFilter}
            durationFilterList={durationFilterList}
            setDurationFilter={setDurationFilter}
          />
        ) : null}
        <MediaLibraryItemList
          fetchNextPage={fetchNextPage as unknown as () => Promise<void>}
          hasNextPage={hasNextPage}
          isCustomDragLayer={isCustomDragLayer}
          isDraggingDisabled={isDraggingDisabled}
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          medias={mediasList}
        />
      </div>
    </React.Fragment>
  );
}

export default MediaLibrary;
